import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

export interface ExplanationSection {
  id?: string;
  title: string;
  videoUrl: string;
  description?: string;
  order: number;
}

export interface ExplanationPage {
  id: string; // Firestore document ID
  pageId?: string; // Custom page identifier (e.g., 'dashboard', 'connecties')
  title: string;
  sections: ExplanationSection[];
  order: number;
  createdAt?: any;
  updatedAt?: any;
}

const COLLECTION_NAME = 'explanationContent';

/**
 * Get all explanation pages
 */
export async function getAllExplanationPages(): Promise<ExplanationPage[]> {
  const q = query(collection(db, COLLECTION_NAME), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      sections: (data.sections || []).sort((a: ExplanationSection, b: ExplanationSection) => 
        (a.order || 0) - (b.order || 0)
      )
    } as ExplanationPage;
  });
}

/**
 * Get a single explanation page by ID
 */
export async function getExplanationPage(pageId: string): Promise<ExplanationPage | null> {
  const docRef = doc(db, COLLECTION_NAME, pageId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return {
    id: docSnap.id,
    ...docSnap.data()
  } as ExplanationPage;
}

/**
 * Create a new explanation page
 */
export async function createExplanationPage(
  page: Omit<ExplanationPage, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    ...page,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  
  return docRef.id;
}

/**
 * Update an explanation page
 */
export async function updateExplanationPage(
  pageId: string,
  updates: Partial<Omit<ExplanationPage, 'id' | 'createdAt'>> & { updatedAt?: any }
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, pageId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete an explanation page
 */
export async function deleteExplanationPage(pageId: string): Promise<void> {
  // First, get the page to delete associated videos
  const page = await getExplanationPage(pageId);
  if (page) {
    // Delete all video files from storage
    for (const section of page.sections) {
      if (section.videoUrl && section.videoUrl.startsWith('https://firebasestorage.googleapis.com')) {
        try {
          // Extract path from URL and delete
          const urlParts = section.videoUrl.split('/o/');
          if (urlParts.length > 1) {
            const pathPart = urlParts[1].split('?')[0];
            const decodedPath = decodeURIComponent(pathPart);
            const videoRef = ref(storage, decodedPath);
            await deleteObject(videoRef);
          }
        } catch (error) {
          console.error('Error deleting video:', error);
          // Continue even if video deletion fails
        }
      }
    }
  }
  
  const docRef = doc(db, COLLECTION_NAME, pageId);
  await deleteDoc(docRef);
}

/**
 * Upload a video file to Firebase Storage
 */
export async function uploadExplanationVideo(
  file: File,
  pageId: string,
  sectionId?: string
): Promise<string> {
  const fileName = `${pageId}/${sectionId || Date.now()}_${file.name}`;
  const storageRef = ref(storage, `explanationVideos/${fileName}`);
  
  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);
  
  return downloadURL;
}

/**
 * Delete a video from Firebase Storage
 */
export async function deleteExplanationVideo(videoUrl: string): Promise<void> {
  if (!videoUrl.startsWith('https://firebasestorage.googleapis.com')) {
    return; // Not a Firebase Storage URL
  }
  
  try {
    const urlParts = videoUrl.split('/o/');
    if (urlParts.length > 1) {
      const pathPart = urlParts[1].split('?')[0];
      const decodedPath = decodeURIComponent(pathPart);
      const videoRef = ref(storage, decodedPath);
      await deleteObject(videoRef);
    }
  } catch (error) {
    console.error('Error deleting video:', error);
    throw error;
  }
}
